import {
  CallMade as CallMadeIcon,
  CallMerge as CallMergeIcon,
  CallMissed as CallMissedIcon,
  CallMissedOutgoing as CallMissedOutgoingIcon,
  CallReceived as CallReceivedIcon,
  CallSplit as CallSplitIcon,
  PhoneDisabled as PhoneDisabledIcon,
  PhoneForwarded as PhoneForwardedIcon,
  PhoneInTalk as PhoneInTalkIcon,
  Voicemail as VoicemailIcon,
} from "@mui/icons-material";
import type { SvgIconComponent } from "@mui/icons-material";
import type {
  MxCallStatus,
  MxCallType,
} from "../../../clients/graphqlTypes.js";

type MxCallIconColor = "error" | "warning" | "success" | "primary";

// Avatar background colour by call type, mirroring the mx-call dashboard's
// CALL_TYPE_COLORS (keyed on call type only, not status). A missing type is
// treated as `incoming`, and any unknown value falls back to `primary.main`.
const CALL_TYPE_AVATAR_COLOR: Record<string, string> = {
  incoming: "success.main",
  outgoing: "primary.main",
  missed: "error.main",
  missed_outgoing: "error.main",
  merge: "info.main",
  split: "info.main",
  transfer: "warning.main",
  voicemail: "warning.main",
};

export function getMxCallAvatarColor(type?: MxCallType | null): string {
  return CALL_TYPE_AVATAR_COLOR[type ?? "incoming"] ?? "primary.main";
}

// Single source of truth for the inline call type/status icon and its colour.
export function getMxCallIcon({
  type,
  status,
}: {
  type?: MxCallType | null;
  status?: MxCallStatus | null;
}): { Icon: SvgIconComponent; color: MxCallIconColor } {
  if (status === "failed") return { Icon: PhoneDisabledIcon, color: "error" };
  // Check `missed_outgoing` first: such calls can also carry `status: "missed"`,
  // which would otherwise match the generic missed branch below.
  if (type === "missed_outgoing")
    return { Icon: CallMissedOutgoingIcon, color: "error" };
  if (status === "missed" || type === "missed")
    return { Icon: CallMissedIcon, color: "error" };
  if (type === "voicemail" || status === "voicemail")
    return { Icon: VoicemailIcon, color: "warning" };
  if (status === "calling" || status === "in_progress")
    return { Icon: PhoneInTalkIcon, color: "success" };
  if (type === "transfer")
    return { Icon: PhoneForwardedIcon, color: "primary" };
  if (type === "merge") return { Icon: CallMergeIcon, color: "primary" };
  if (type === "split") return { Icon: CallSplitIcon, color: "primary" };
  if (type === "outgoing") return { Icon: CallMadeIcon, color: "primary" };
  return { Icon: CallReceivedIcon, color: "primary" };
}

export function MxCallTypeIcon({
  type,
  status,
}: {
  type?: MxCallType | null;
  status?: MxCallStatus | null;
}) {
  const { Icon, color } = getMxCallIcon({ type, status });
  return <Icon color={color} fontSize="small" />;
}
